import { useEffect, useState } from 'react';
import { userService } from '../services/firebase';
import { User } from '../types';
import { useAuth } from '../hooks/useAuth';
import LoadingSpinner from '../components/LoadingSpinner';
import ConfirmDialog from '../components/ConfirmDialog';

const ROLES = ['superadmin', 'admin', 'agent', 'customer'] as const;

export default function UserManagement() {
  const { user } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [form, setForm] = useState<Partial<User>>({});
  const [pendingDelete, setPendingDelete] = useState<User | null>(null);

  useEffect(() => {
    if (user?.role !== 'superadmin') return;
    userService.getAllUsers().then(setUsers).finally(() => setLoading(false));
  }, [user]);

  if (!user || user.role !== 'superadmin') {
    return <div className="p-6 text-red-600">Access denied. Superadmin only.</div>;
  }

  const handleEdit = (u: User) => {
    setEditingUser(u);
    setForm({ ...u });
  };

  const handleDelete = async (uid: string) => {
    setPendingDelete(null);
    await userService.deleteUser(uid);
    setUsers(users.filter(u => u.id !== uid));
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingUser) return;
    await userService.updateUser(editingUser.id, form);
    setUsers(users.map(u => u.id === editingUser.id ? { ...u, ...form } as User : u));
    setEditingUser(null);
    setForm({});
  };

  const ROLE_STYLES: Record<string, string> = {
    superadmin: 'bg-purple-100 text-purple-800',
    admin: 'bg-blue-100 text-blue-800',
    agent: 'bg-teal-100 text-teal-800',
    customer: 'bg-gray-100 text-gray-800',
  };

  return (
    <div className="mx-auto max-w-4xl">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">User Management</h1>
        <p className="mt-1 text-gray-600">Roles control what each person can see and do.</p>
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <LoadingSpinner />
        </div>
      ) : users.length === 0 ? (
        <div className="rounded-lg bg-white py-12 text-center shadow">
          <p className="text-gray-500">No users found.</p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg bg-white shadow">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">Name</th>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">Email</th>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">Role</th>
                <th className="px-6 py-3 text-right text-xs font-semibold uppercase tracking-wider text-gray-500">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {users.map(u => (
                <tr key={u.id} className="hover:bg-gray-50">
                  <td className="whitespace-nowrap px-6 py-4 text-sm font-medium text-gray-900">
                    {u.name || <span className="italic text-gray-400">No name</span>}
                  </td>
                  <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">{u.email}</td>
                  <td className="whitespace-nowrap px-6 py-4">
                    <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold capitalize ${ROLE_STYLES[u.role] || 'bg-gray-100 text-gray-800'}`}>
                      {u.role}
                    </span>
                  </td>
                  <td className="whitespace-nowrap px-6 py-4 text-right text-sm">
                    <button
                      className="font-medium text-blue-600 hover:text-blue-800"
                      onClick={() => handleEdit(u)}
                    >
                      Edit
                    </button>
                    <button
                      className="ml-4 font-medium text-red-600 hover:text-red-800 disabled:cursor-not-allowed disabled:text-gray-300"
                      onClick={() => setPendingDelete(u)}
                      disabled={u.id === user.id}
                      title={u.id === user.id ? "You can't remove your own account" : undefined}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editingUser && (
        <form className="mt-6 space-y-5 rounded-lg bg-white p-6 shadow" onSubmit={handleSubmit}>
          <h2 className="text-lg font-semibold text-gray-900">
            Edit {editingUser.email}
          </h2>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Name</label>
            <input name="name" className="input" value={form.name || ''} onChange={handleChange} />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Role</label>
            <select name="role" className="input" value={form.role || ''} onChange={handleChange}>
              {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
          <div className="flex gap-2">
            <button className="btn-primary" type="submit">Save</button>
            <button className="btn-outline" type="button" onClick={() => setEditingUser(null)}>Cancel</button>
          </div>
        </form>
      )}

      <ConfirmDialog
        open={Boolean(pendingDelete)}
        tone="danger"
        title="Remove this user?"
        confirmLabel="Remove user"
        message={
          pendingDelete ? (
            <>
              This removes <strong>{pendingDelete.email}</strong> from the user list and revokes
              their role.
              <span className="mt-2 block font-medium text-red-700">
                Note: this deletes the user record only. Their sign-in account still exists in
                Firebase Authentication and must be removed there separately.
              </span>
            </>
          ) : null
        }
        onConfirm={() => pendingDelete && handleDelete(pendingDelete.id)}
        onCancel={() => setPendingDelete(null)}
      />
    </div>
  );
} 